import React from 'react';
import { Contact } from '@/types/Contact';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * ContactDisplay Component
 *
 * A reusable component that displays contact information, showing the contact name
 * if available, otherwise falling back to the phone number (caller_id).
 *
 * Features:
 * - Automatic fallback from name to phone number
 * - Multiple display variants (default, compact, detailed)
 * - Optional phone number display alongside name
 * - Built-in support for click and hover events
 * - Responsive design with customizable styling
 *
 * @example
 * // Basic usage - shows name if available, phone number otherwise
 * <ContactDisplay
 *   callerId="+1234567890"
 *   agentId={1}
 *   contacts={agentContacts}
 * />
 *
 * @example
 * // With phone number fallback and click handler
 * <ContactDisplay
 *   callerId="+1234567890"
 *   agentId={1}
 *   contacts={agentContacts}
 *   showPhoneNumber={true}
 *   onClick={() => openContactDetails()}
 *   variant="detailed"
 * />
 */

interface ContactDisplayProps {
  callerId: string;
  agentId?: number;
  contacts?: Contact[] | null;
  className?: string;
  showPhoneNumber?: boolean; // Whether to show phone number as fallback when name exists
  variant?: 'default' | 'compact' | 'detailed' | 'inline';
  onClick?: () => void;
  onHover?: () => void;
}

export const ContactDisplay: React.FC<ContactDisplayProps> = ({
  callerId,
  agentId,
  contacts,
  className,
  showPhoneNumber = false,
  variant = 'default',
  onClick,
  onHover,
}) => {
  // Find the contact by caller_id and agent_id
  const contact = (contacts ?? []).find(
    c => c.caller_id === callerId && c.agent_id === agentId
  );

  const hasName = contact?.name && contact.name.trim() !== '';
  const displayName = hasName ? contact.name : callerId;
  const shouldShowPhone = showPhoneNumber && hasName;

  const baseClasses = cn(
    'inline-flex items-center gap-2 font-semibold',
    onClick && 'cursor-pointer hover:underline',
    className
  );

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleMouseEnter = () => {
    if (onHover) {
      onHover();
    }
  };

  // Helper function to render contact name with tooltip
  const renderContactName = (name: string, phone: string) => {
    if (hasName) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-foreground">{name}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{phone}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return <span>{name}</span>;
  };

  if (variant === 'compact') {
    return (
      <span
        className={baseClasses}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
      >
        {renderContactName(displayName, callerId)}
        {shouldShowPhone && (
          <span className="text-xs text-muted-foreground">({callerId})</span>
        )}
      </span>
    );
  }

  if (variant === 'inline') {
    return (
      <span
        className={cn(baseClasses, 'text-base md:text-xl')}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
      >
        {renderContactName(displayName, callerId)}
        {shouldShowPhone && (
          <span className="ml-1 text-xs text-muted-foreground md:text-sm">
            • {callerId}
          </span>
        )}
      </span>
    );
  }

  if (variant === 'detailed') {
    return (
      <div
        className={baseClasses}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
      >
        <div className="flex flex-col">
          <span>{renderContactName(displayName, callerId)}</span>
          {shouldShowPhone && (
            <span className="text-xs text-muted-foreground">{callerId}</span>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <span
      className={baseClasses}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      {renderContactName(displayName, callerId)}
      {shouldShowPhone && (
        <span className="ml-1 text-sm text-muted-foreground">• {callerId}</span>
      )}
    </span>
  );
};

export default ContactDisplay;
